import { company } from '../data/company';
import type { Product } from '../types';

/**
 * SEO helpers for the public marketing site. SITE_URL is the canonical origin (override per
 * environment with VITE_SITE_URL). Structured-data builders produce plain JSON-LD objects that the
 * <Seo> component injects into the head.
 */
export const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://greenbaybio.com').replace(/\/$/, '');
export const DEFAULT_OG = '/logo.png';

export function absoluteUrl(path = '/'): string {
  if (path.startsWith('http')) return path;
  return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: company.legalName,
    alternateName: company.name,
    url: SITE_URL,
    logo: absoluteUrl('/logo.png'),
    email: company.email,
    telephone: company.phoneHref,
    description: company.description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: `${company.address.line1}, ${company.address.line2}`,
      addressLocality: company.address.city,
      postalCode: company.address.postcode,
      addressCountry: 'IN',
    },
  };
}

export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: company.legalName,
    image: absoluteUrl('/logo.png'),
    url: SITE_URL,
    telephone: company.phoneHref,
    email: company.email,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: `${company.address.line1}, ${company.address.line2}`,
      addressLocality: company.address.city,
      postalCode: company.address.postcode,
      addressCountry: 'IN',
    },
    areaServed: 'IN',
  };
}

export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function productSchema(product: Product) {
  const image = product.variants.find((v) => v.image)?.image;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.shortDescription,
    ...(image ? { image: absoluteUrl(image) } : {}),
    brand: { '@type': 'Brand', name: company.name },
    category: product.categorySlug,
    manufacturer: { '@type': 'Organization', name: company.legalName },
    additionalProperty: (product.specifications ?? []).map((s) => ({
      '@type': 'PropertyValue',
      name: s.parameter,
      value: s.value,
    })),
    hasVariant: product.variants.map((v) => ({
      '@type': 'Product',
      name: `${product.name} ${v.packSize}`,
      ...(v.sku ? { sku: v.sku } : {}),
    })),
  };
}

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}
